import { useEffect, useRef, useState, useCallback } from 'react';
import { APIProvider, useApiIsLoaded } from '@vis.gl/react-google-maps';
import { Capacitor } from '@capacitor/core';
import type { NearbyUser } from "@/hooks/useNearbyUsers";
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Gift, MapPin, Clock, X, Ticket } from 'lucide-react';
import { calculateBearing, calculateDistanceMeters } from '@/lib/streetViewUtils';
import { formatDistance } from '@/lib/distance';
import { isOnline, formatLastSeen } from '@/lib/time';
import { SaveUserButton } from './SaveUserButton';

import { useNavigate } from 'react-router-dom';

interface StreetViewMapProps {
  users: NearbyUser[];
  currentUserLocation: { lat: number; lng: number };
  currentUserId?: string | null;
  onSendGift?: (userId: string) => void;
  onClose?: () => void;
}

interface UserOverlay {
  user: NearbyUser;
  element: HTMLDivElement;
}

const MAX_VISIBLE_DISTANCE = 200; // meters - only show users within this distance

const StreetViewContent = ({ 
  users, 
  currentUserLocation, 
  currentUserId, 
  onSendGift,
  onClose 
}: StreetViewMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const overlaysRef = useRef<UserOverlay[]>([]);
  const [selectedUser, setSelectedUser] = useState<NearbyUser | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const navigate = useNavigate();

  // Filter users to show only those within visible distance and exclude current user
  const visibleUsers = users.filter(user => {
    if (user.id === currentUserId) return false;
    if (user.lat == null || user.lng == null) return false;
    const distance = calculateDistanceMeters(
      currentUserLocation.lat,
      currentUserLocation.lng,
      user.lat,
      user.lng
    );
    return distance <= MAX_VISIBLE_DISTANCE;
  });

  // Create floating avatar element
  const createAvatarElement = useCallback((user: NearbyUser): HTMLDivElement => {
    const wrapper = document.createElement('div');
    wrapper.className = 'street-view-avatar';
    wrapper.style.cssText = `
      position: absolute;
      transform: translate(-50%, -50%);
      cursor: pointer;
      transition: transform 0.15s ease-out;
      z-index: 100;
      pointer-events: auto;
    `;

    const avatar = document.createElement('div');
    avatar.style.cssText = `
      width: 56px;
      height: 56px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 4px 20px rgba(0,0,0,0.4);
      overflow: hidden;
      background: linear-gradient(135deg, hsl(24 100% 65%), hsl(336 80% 60%));
      animation: float 3s ease-in-out infinite;
    `;

    if (user.avatar_url) {
      const img = document.createElement('img');
      img.src = user.avatar_url;
      img.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
      img.onerror = () => {
        img.remove();
        const fallback = document.createElement('div');
        fallback.textContent = user.name?.[0] || '?';
        fallback.style.cssText = `
          width: 100%; height: 100%; display: flex; align-items: center; 
          justify-content: center; font-size: 24px; font-weight: bold; color: white;
        `;
        avatar.appendChild(fallback);
      };
      avatar.appendChild(img);
    } else {
      const fallback = document.createElement('div');
      fallback.textContent = user.name?.[0] || '?';
      fallback.style.cssText = `
        width: 100%; height: 100%; display: flex; align-items: center; 
        justify-content: center; font-size: 24px; font-weight: bold; color: white;
      `;
      avatar.appendChild(fallback);
    }

    // Online indicator
    if (isOnline(user.location_updated_at)) {
      const indicator = document.createElement('div');
      indicator.style.cssText = `
        position: absolute; bottom: 2px; right: 2px; width: 14px; height: 14px;
        background: #22c55e; border: 2px solid white; border-radius: 50%;
      `;
      wrapper.appendChild(indicator);
    }

    // Name label
    const label = document.createElement('div');
    label.textContent = user.name || 'User';
    label.style.cssText = `
      margin-top: 4px; padding: 2px 8px; background: rgba(0,0,0,0.7);
      color: white; font-size: 12px; font-weight: 500; border-radius: 4px;
      white-space: nowrap; text-align: center;
    `;

    wrapper.appendChild(avatar);
    wrapper.appendChild(label);
    wrapper.addEventListener('click', () => setSelectedUser(user));

    return wrapper;
  }, []);

  // Update avatar positions based on POV
  const updateAvatarPositions = useCallback(() => {
    if (!panoramaRef.current) return;

    const pov = panoramaRef.current.getPov();
    const containerWidth = containerRef.current?.clientWidth || 0;
    const containerHeight = containerRef.current?.clientHeight || 0;

    overlaysRef.current.forEach(({ user, element }) => {
      if (user.lat == null || user.lng == null) {
        element.style.display = 'none';
        return;
      }

      // Calculate bearing from current position to user
      const bearing = calculateBearing(
        currentUserLocation.lat,
        currentUserLocation.lng,
        user.lat,
        user.lng
      );

      // Calculate angle difference from current heading
      let angleDiff = bearing - pov.heading;
      
      // Normalize to -180 to 180
      while (angleDiff > 180) angleDiff -= 360;
      while (angleDiff < -180) angleDiff += 360;

      // Check if user is in field of view (roughly 90 degrees each side)
      const fov = 90;
      if (Math.abs(angleDiff) > fov) {
        element.style.display = 'none';
        return;
      }

      // Calculate screen X position
      const x = containerWidth / 2 + (angleDiff / fov) * (containerWidth / 2);

      // Calculate distance for Y position and scale
      const distance = calculateDistanceMeters(
        currentUserLocation.lat,
        currentUserLocation.lng,
        user.lat,
        user.lng
      );

      // Y position based on pitch and distance (farther = higher on screen)
      const distanceFactor = 1 - Math.min(distance / MAX_VISIBLE_DISTANCE, 1);
      const baseY = containerHeight * 0.5;
      const pitchOffset = (pov.pitch / 90) * (containerHeight * 0.3);
      const y = baseY + pitchOffset - (distanceFactor * containerHeight * 0.1);

      // Scale based on distance
      const scale = 0.5 + distanceFactor * 0.5;

      element.style.display = 'block';
      element.style.left = `${x}px`;
      element.style.top = `${y}px`;
      element.style.transform = `translate(-50%, -50%) scale(${scale})`;
      element.style.opacity = String(0.6 + distanceFactor * 0.4);
    });

    animationFrameRef.current = requestAnimationFrame(updateAvatarPositions);
  }, [currentUserLocation]);

  // Initialize Street View
  useEffect(() => {
    if (!containerRef.current || !window.google) return;

    const panorama = new window.google.maps.StreetViewPanorama(containerRef.current, {
      position: currentUserLocation,
      pov: { heading: 0, pitch: 0 },
      zoom: 1,
      addressControl: false,
      showRoadLabels: false,
      motionTracking: false,
      motionTrackingControl: false,
    });

    panoramaRef.current = panorama;

    // Create overlay container
    const overlayContainer = document.createElement('div');
    overlayContainer.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      pointer-events: none; overflow: hidden; z-index: 50;
    `;
    containerRef.current.appendChild(overlayContainer);

    // Create avatar overlays
    overlaysRef.current = visibleUsers.map(user => {
      const element = createAvatarElement(user);
      overlayContainer.appendChild(element);
      return { user, element };
    });

    // Start position updates
    updateAvatarPositions();

    // Listen for POV changes
    panorama.addListener('pov_changed', updateAvatarPositions);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      overlayContainer.remove();
      panoramaRef.current = null;
    };
  }, [currentUserLocation, visibleUsers, createAvatarElement, updateAvatarPositions]);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full" />
      
      {/* Close button */}
      <Button
        variant="secondary"
        size="sm"
        className="absolute top-4 right-4 z-[200] shadow-lg"
        onClick={onClose}
      >
        <X className="w-4 h-4 mr-1" />
        Exit Street View
      </Button>

      {/* Selected user card */}
      {selectedUser && (
        <Card className="absolute bottom-4 left-4 right-4 z-[200] p-4 shadow-lg bg-background/95 backdrop-blur">
          <div className="flex items-center gap-3">
            <Avatar className="w-14 h-14 border-2 border-border">
              <AvatarImage src={selectedUser.avatar_url || undefined} />
              <AvatarFallback>{selectedUser.name?.[0] || '?'}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg truncate">{selectedUser.name || 'User'}</h3>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {isOnline(selectedUser.location_updated_at) ? 'Online' : formatLastSeen(selectedUser.location_updated_at)}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {formatDistance(calculateDistanceMeters(
                    currentUserLocation.lat,
                    currentUserLocation.lng,
                    selectedUser.lat!,
                    selectedUser.lng!
                  ) / 1000)}
                </span>
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setSelectedUser(null)}
              className="shrink-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex gap-2 mt-3">
            {selectedUser.name?.toLowerCase().startsWith('ambassador') && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(`/prize-draw?user=${selectedUser.id}`)}
              >
                <Ticket className="w-4 h-4 mr-2" />
                Prize Draw
              </Button>
            )}
            <Button
              className="flex-1"
              onClick={() => {
                if (onSendGift) onSendGift(selectedUser.id);
              }}
            >
              <Gift className="w-4 h-4 mr-2" />
              Send Gift
            </Button>
            <SaveUserButton userId={selectedUser.id} userName={selectedUser.name || 'User'} size="default" />
          </div>
        </Card>
      )}

      {/* Floating animation keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
};

const StreetViewLoadedGate = (props: StreetViewMapProps) => {
  const apiIsLoaded = useApiIsLoaded();
  if (!apiIsLoaded) {
    return <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">Loading map…</div>;
  }
  return <StreetViewContent {...props} />;
};

const StreetViewMap = (props: StreetViewMapProps) => {
  const isNative = Capacitor.isNativePlatform?.() ?? (Capacitor.getPlatform?.() !== 'web');
  const apiKey = (isNative ? (import.meta.env.VITE_GOOGLE_MAPS_API_KEY_NATIVE as string | undefined) : undefined) || (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string);
  return (
    <APIProvider apiKey={apiKey} authReferrerPolicy="origin">
      <StreetViewLoadedGate {...props} />
    </APIProvider>
  );
};

export default StreetViewMap;
